import { Box, Stack, Typography, useTheme } from "@mui/material";
import { type ReactNode, useRef, useState } from "react";
import type { AiCapability, PricingPlan } from "../../types";
import {
  AgentActionsIcon,
  AI_CAPABILITIES,
  AI_SALES_FORM_URL,
  AiCapabilityCard,
  AiPriceCard,
  HelenaCapabilityIcon,
  PlanCard,
  PRICING_COPY,
  PRICING_PLANS,
  PricingEyebrow,
  PricingGlow,
  SALES_FORM_URL,
  ScrollDownButton,
} from "../pricing";
import { PRICING_DESIGN_VALUES } from "../pricing/pricingDesignValues";
import { ManageSeatsDrawer } from "./drawers";

const AI_CAPABILITY_ICONS: Record<AiCapability["key"], ReactNode> = {
  agentActions: <AgentActionsIcon />,
  helena: <HelenaCapabilityIcon />,
};

const openSalesForm = () => {
  window.open(SALES_FORM_URL, "_blank", "noopener,noreferrer");
};

const openAiSalesForm = () => {
  window.open(AI_SALES_FORM_URL, "_blank", "noopener,noreferrer");
};

export const OverviewPricing = () => {
  const theme = useTheme();
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const aiSectionRef = useRef<HTMLDivElement>(null);

  const handlePlanCtaClick = (plan: PricingPlan) => {
    if (plan.key === "enterprise") {
      openSalesForm();
      return;
    }
    setSelectedPlan(plan);
  };

  return (
    <Box
      sx={{
        position: "relative",
        isolation: "isolate",
        padding: "64px 0 93px",
        // Follows PricingPageFrame's rounded panel (inherit is 0 elsewhere).
        borderRadius: "inherit",
      }}
    >
      <PricingGlow />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "calc(100% - 48px)",
          maxWidth: "1144px",
          marginInline: "auto",
        }}
      >
        <Stack
          alignItems="center"
          sx={{
            gap: "8px",
            maxWidth: "676px",
            marginInline: "auto",
            textAlign: "center",
          }}
        >
          <PricingEyebrow
            label={PRICING_COPY.plansEyebrow}
            gradient={PRICING_DESIGN_VALUES.plansEyebrowGradient}
          />
          <Typography variant="h1">{PRICING_COPY.plansTitle}</Typography>
          <Typography
            variant="body-large"
            sx={{ color: theme.palette.lightBg.low }}
          >
            {PRICING_COPY.plansSubtitle}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            alignItems: "stretch",
            gap: "16px",
            marginTop: "54px",
          }}
        >
          {PRICING_PLANS.map((plan) => (
            <PlanCard
              key={plan.key}
              plan={plan}
              onCtaClick={handlePlanCtaClick}
            />
          ))}
        </Box>

        <Stack
          ref={aiSectionRef}
          alignItems="center"
          sx={{ gap: "48px", marginTop: "56px" }}
        >
          <Stack alignItems="center" sx={{ gap: "8px", textAlign: "center" }}>
            <PricingEyebrow
              label={PRICING_COPY.aiEyebrow}
              gradient={PRICING_DESIGN_VALUES.aiEyebrowGradient}
            />
            <Typography variant="h1" component="h2">
              {PRICING_COPY.aiTitle}
            </Typography>
          </Stack>

          <AiPriceCard onCtaClick={openAiSalesForm} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "24px",
              width: "100%",
            }}
          >
            {AI_CAPABILITIES.map((capability) => (
              <AiCapabilityCard
                key={capability.key}
                capability={capability}
                icon={AI_CAPABILITY_ICONS[capability.key]}
              />
            ))}
          </Box>
        </Stack>
      </Box>

      <ScrollDownButton targetRef={aiSectionRef} />

      {selectedPlan && (
        <ManageSeatsDrawer
          open={true}
          onClose={() => setSelectedPlan(null)}
          subscriptionDetails={null}
          type="CREATE"
          billingPeriod={
            selectedPlan.key === "monthly" ? "monthly" : "annually"
          }
          price={selectedPlan.price}
        />
      )}
    </Box>
  );
};
